import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const AccountDetailsStep = ({ formData, errors, onChange }) => {
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z0-9]/?.test(password)) strength++;

    const levels = [
      { strength: 0, label: 'Very Weak', color: 'bg-error' },
      { strength: 1, label: 'Weak', color: 'bg-warning' },
      { strength: 2, label: 'Fair', color: 'bg-warning' },
      { strength: 3, label: 'Good', color: 'bg-success' },
      { strength: 4, label: 'Strong', color: 'bg-success' },
      { strength: 5, label: 'Very Strong', color: 'bg-success' }
    ];

    return levels?.[strength];
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  return (
    <div className="space-y-4 md:space-y-5 lg:space-y-6">
      <div className="bg-card rounded-xl p-4 md:p-5 lg:p-6 border border-border">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="UserPlus" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground mb-1">
              Create Your Account
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Enter your basic information to get started with Maladapted Miscreants
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Input
            label="Username"
            type="text"
            name="username"
            placeholder="Choose a unique username"
            value={formData?.username}
            onChange={onChange}
            error={errors?.username}
            required
            description="This will be your display name in the guild"
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={onChange}
            error={errors?.email}
            required
            description="We'll send verification and important guild updates here"
          />

          <div>
            <Input
              label="Password"
              type="password"
              name="password"
              placeholder="Create a strong password"
              value={formData?.password}
              onChange={onChange}
              error={errors?.password}
              required
              description="Minimum 8 characters with letters, numbers, and symbols"
            />
            
            {formData?.password && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs md:text-sm text-muted-foreground">
                    Password Strength:
                  </span>
                  <span className={`text-xs md:text-sm font-medium ${
                    passwordStrength?.strength >= 3 ? 'text-success' : 
                    passwordStrength?.strength >= 2 ? 'text-warning' : 'text-error'
                  }`}>
                    {passwordStrength?.label}
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                    style={{ width: `${(passwordStrength?.strength / 5) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <Input
            label="Confirm Password"
            type="password"
            name="confirmPassword"
            placeholder="Re-enter your password"
            value={formData?.confirmPassword}
            onChange={onChange}
            error={errors?.confirmPassword}
            required
          />
        </div>
      </div>
      <div className="bg-accent/10 rounded-xl p-4 md:p-5 border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm md:text-base text-foreground">
            <p className="font-medium mb-1">Account Security Tips</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Use a unique password you don't use elsewhere</li>
              <li>• Include uppercase, lowercase, numbers, and symbols</li>
              <li>• Avoid personal information like names or birthdays</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetailsStep;